import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PropertyCard from '@/components/PropertyCard';
import { getUserProfile, getProperties, toggleSaveProperty, getSavedProperties } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Building2, MapPin, Phone, Mail, MessageSquare, Calendar, Star, CheckCircle } from 'lucide-react';

const ProfilePage = () => {
  const { userId } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [properties, setProperties] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, [userId, user]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const profileData = await getUserProfile(userId);
      setProfile(profileData);

      const propsData = await getProperties({ limit: 100 });
      const userProperties = propsData.filter(p => p.user_id === userId);
      setProperties(userProperties);

      if (user) {
        const saved = await getSavedProperties();
        setSavedIds(new Set(saved.map(p => p.property_id)));
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (propertyId) => {
    if (!user) {
      toast.error('Please login to save properties');
      return;
    }
    try {
      const result = await toggleSaveProperty(propertyId);
      setSavedIds(prev => {
        const newSet = new Set(prev);
        if (result.saved) {
          newSet.add(propertyId);
          toast.success('Property saved');
        } else {
          newSet.delete(propertyId);
          toast.info('Property removed from saved');
        }
        return newSet;
      });
    } catch (error) {
      toast.error('Failed to save property');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F172A]"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-2">User Not Found</h2>
          <p className="text-muted-foreground mb-4">The profile you're looking for doesn't exist</p>
          <Link to="/properties">
            <Button className="btn-primary">Browse Properties</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <div className="container-luxury">
        {/* Profile Header */}
        <div className="bg-white rounded-sm shadow-sm border border-border/50 p-8 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Avatar */}
            <div className="h-24 w-24 rounded-full bg-[#0F172A] flex items-center justify-center flex-shrink-0">
              {profile.picture ? (
                <img src={profile.picture} alt={profile.name} className="h-24 w-24 rounded-full object-cover" />
              ) : (
                <span className="text-white text-3xl font-bold">{profile.name?.[0]?.toUpperCase()}</span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A]">{profile.name}</h1>
                {profile.verified && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                    <CheckCircle className="h-3 w-3" /> Verified
                  </div>
                )}
              </div>
              
              <p className="text-muted-foreground capitalize mb-4">
                {profile.user_type === 'agent' ? 'Real Estate Agent' :
                 profile.user_type === 'seller' ? 'Property Owner' : 'Buyer / Tenant'}
              </p>

              <div className="flex flex-wrap gap-6 text-sm">
                {profile.email && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" /> {profile.email}
                  </div>
                )}
                {profile.phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" /> {profile.phone}
                  </div>
                )}
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="h-4 w-4" /> {profile.listings_count || properties.length} Listings
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" /> Member since {new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </div>
              </div>
            </div>

            {/* Contact Button */}
            {user && user.user_id !== userId && (
              <Link to={`/messages/${userId}`}>
                <Button className="btn-primary" data-testid="contact-user-btn">
                  <MessageSquare className="h-4 w-4 mr-2" /> Message
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Properties */}
        <div>
          <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A] mb-6">
            {properties.length} {properties.length === 1 ? 'Property' : 'Properties'} Listed
          </h2>

          {properties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property) => (
                <PropertyCard
                  key={property.property_id}
                  property={property}
                  onSave={handleSave}
                  isSaved={savedIds.has(property.property_id)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-sm border border-border/50">
              <Building2 className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-xl font-medium text-[#0F172A] mb-2">No Properties Listed</h3>
              <p className="text-muted-foreground">This user hasn't listed any properties yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
